import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, Eye, Phone, MessageCircle, Calendar, Award, Zap, X } from "lucide-react";
import { api, formatPrice, asItems, asObject, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  count, formatDateTime, StatusPill, WorkbenchError, WorkbenchHeader,
} from "@/components/admin/AdminWorkbenchUi";

const BOOST_OPTIONS = [
  { v: 7, l: "7 days" },
  { v: 14, l: "14 days" },
  { v: 30, l: "30 days" },
  { v: 90, l: "90 days" },
];

export default function AdminAnalytics() {
  const [overview, setOverview] = useState(null);
  const [performance, setPerformance] = useState([]);
  const [boosted, setBoosted] = useState([]);
  const [boostTarget, setBoostTarget] = useState(null);
  const [boostDays, setBoostDays] = useState(7);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [a, b, c] = await Promise.allSettled([
        api.get("/v1/admin/analytics/overview"),
        api.get("/admin-bff/analytics/property-performance"),
        api.get("/v1/admin/properties/boosted"),
      ]);
      setOverview(a.status === "fulfilled" ? asObject(a.value.data) : {});
      setPerformance(b.status === "fulfilled" ? asItems(b.value.data) : []);
      setBoosted(c.status === "fulfilled" ? asItems(c.value.data) : []);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const applyBoost = async () => {
    if (!boostTarget) return;
    setBusy(true);
    try {
      await api.post(`/v1/admin/properties/${boostTarget.property_id}/boost`, { days: boostDays });
      toast.success(`Boosted for ${boostDays} days`);
      setBoostTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const removeBoost = async (pid) => {
    setBusy(true);
    try {
      await api.delete(`/v1/admin/properties/${pid}/boost`);
      toast.success("Boost removed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-analytics-page">
      <WorkbenchHeader
        eyebrow="Platform insights"
        title="Analytics Dashboard"
        description="Properties, leads, Plots on Demand, bookings and listing performance at a glance."
      />

      {error && <WorkbenchError message={error} onRetry={load} />}

      {/* Overview tiles */}
      <section data-testid="admin-overview-tiles">
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[...Array(8)].map((_, i) => <div key={i} className="h-24 shimmer rounded-lg"/>)}
          </div>
        ) : overview ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Tile label="Total Properties" value={overview.total_properties} accent="primary" testid="tile-total"/>
            <Tile label="Published" value={overview.published} accent="emerald" testid="tile-published"/>
            <Tile label="Pending Verification" value={overview.pending_verification} accent="amber" testid="tile-pending"/>
            <Tile label="Verified (awaiting publish)" value={overview.verified} accent="blue" testid="tile-verified"/>
            <Tile label="Booked" value={overview.booked} accent="indigo" testid="tile-booked"/>
            <Tile label="Sold" value={overview.sold} accent="rose" testid="tile-sold"/>
            <Tile label="Total Leads" value={overview.total_leads} accent="purple" testid="tile-leads"/>
            <Tile label="Plots on Demand" value={overview.plot_demands} accent="slate" testid="tile-demands"/>
          </div>
        ) : null}
      </section>

      {/* Property Performance */}
      <section data-testid="admin-performance-section">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-2xl font-bold text-primary flex items-center gap-2">
            <TrendingUp className="w-5 h-5"/>Top Performing Properties
          </h2>
          <span className="text-xs text-muted-foreground">{performance.length} listings</span>
        </div>
        {loading ? (
          <div className="h-40 shimmer rounded-2xl" aria-label="Loading property performance" />
        ) : performance.length === 0 ? (
          <div className="py-12 text-center border border-dashed border-border rounded-lg text-muted-foreground" data-testid="admin-perf-empty">
            No performance data yet.
          </div>
        ) : (
          <div className="overflow-x-auto border border-border rounded-lg">
            <table className="w-full min-w-[760px] text-sm" data-testid="admin-perf-table">
              <thead className="bg-secondary text-primary">
                <tr>
                  <th className="text-left p-3 font-bold uppercase tracking-wider text-xs">Property</th>
                  <th className="text-right p-3 font-bold uppercase tracking-wider text-xs"><Eye className="inline w-3.5 h-3.5"/> Views</th>
                  <th className="text-right p-3 font-bold uppercase tracking-wider text-xs"><MessageCircle className="inline w-3.5 h-3.5"/> Leads</th>
                  <th className="text-right p-3 font-bold uppercase tracking-wider text-xs"><Phone className="inline w-3.5 h-3.5"/> Reveals</th>
                  <th className="text-right p-3 font-bold uppercase tracking-wider text-xs">WhatsApp</th>
                  <th className="text-right p-3 font-bold uppercase tracking-wider text-xs"><Calendar className="inline w-3.5 h-3.5"/> Visits</th>
                  <th className="text-center p-3 font-bold uppercase tracking-wider text-xs">Status</th>
                  <th className="text-right p-3 font-bold uppercase tracking-wider text-xs">Boost</th>
                </tr>
              </thead>
              <tbody>
                {performance.map((p) => (
                  <tr key={p.property_id} className="border-t border-border hover:bg-secondary/50" data-testid={`perf-row-${p.property_id}`}>
                    <td className="p-3 max-w-xs">
                      <Link to={`/properties/${p.property_id}`} className="font-bold text-primary hover:underline line-clamp-1">{p.title}</Link>
                    </td>
                    <td className="p-3 text-right font-mono">{count(p.views)}</td>
                    <td className="p-3 text-right font-mono">{count(p.leads)}</td>
                    <td className="p-3 text-right font-mono">{count(p.phone_reveals)}</td>
                    <td className="p-3 text-right font-mono">{count(p.whatsapp_clicks)}</td>
                    <td className="p-3 text-right font-mono">{count(p.site_visits)}</td>
                    <td className="p-3 text-center"><StatusPill value={p.status}/></td>
                    <td className="p-3 text-right">
                      {String(p.status || "").toUpperCase() === "PUBLISHED" ? (
                        <Button size="sm" variant="outline" onClick={() => { setBoostTarget(p); setBoostDays(7); }} data-testid={`perf-boost-${p.property_id}`}>
                          <Zap className="w-3.5 h-3.5 mr-1.5" aria-hidden="true"/>Boost
                        </Button>
                      ) : <span className="text-xs text-muted-foreground">Unavailable</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Currently Boosted */}
      <section data-testid="admin-boosted-section">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-2xl font-bold text-primary flex items-center gap-2">
            <Award className="w-5 h-5 text-amber-500"/>Currently Boosted
          </h2>
          <span className="text-xs text-muted-foreground">{boosted.length} boosted</span>
        </div>
        {loading ? (
          <div className="h-32 shimmer rounded-2xl" aria-label="Loading featured properties" />
        ) : boosted.length === 0 ? (
          <div className="py-8 text-center border border-dashed border-border rounded-lg text-muted-foreground text-sm" data-testid="admin-boosted-empty">
            No boosted listings — boost a published property to push it to the top of search.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="admin-boosted-grid">
            {boosted.map(p => (
              <div key={p.property_id} className="rounded-2xl border border-amber-500/30 bg-card p-4 shadow-sm" data-testid={`boosted-row-${p.property_id}`}>
                <div className="flex items-start justify-between gap-2">
                  <Link to={`/properties/${p.property_id}`} className="font-bold text-primary line-clamp-2 hover:underline">{p.title}</Link>
                  <button type="button" onClick={() => removeBoost(p.property_id)} disabled={busy} className="shrink-0 rounded-md p-1 text-muted-foreground transition-colors hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50" title="Remove boost" aria-label={`Remove boost from ${p.title || "property"}`} data-testid={`unboost-${p.property_id}`}>
                    <X className="w-4 h-4" aria-hidden="true"/>
                  </button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">{[p.location?.locality, p.location?.city].filter(Boolean).join(", ") || "Location unavailable"}</p>
                <div className="flex items-center justify-between mt-3 text-xs">
                  <span className="font-bold text-primary">{formatPrice(p.price)}</span>
                  <span className="font-bold uppercase tracking-wider text-amber-700 dark:text-amber-300">
                    {p.boost_days || "—"} day boost · until {formatDateTime(p.boosted_until)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Boost Dialog */}
      <Dialog open={!!boostTarget} onOpenChange={(o) => { if (!o) setBoostTarget(null); }}>
        <DialogContent data-testid="admin-boost-dialog">
          <DialogHeader>
            <DialogTitle>Boost listing to top</DialogTitle>
            <DialogDescription>
              The property will appear at the top of search results until the boost expires.
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm">
            <p className="font-bold text-primary">{boostTarget?.title}</p>
            <div className="mt-4">
              <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Duration</p>
              <Select value={String(boostDays)} onValueChange={(v) => setBoostDays(Number(v))}>
                <SelectTrigger data-testid="admin-boost-duration"><SelectValue/></SelectTrigger>
                <SelectContent>
                  {BOOST_OPTIONS.map(o => <SelectItem key={o.v} value={String(o.v)}>{o.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBoostTarget(null)}>Cancel</Button>
            <Button onClick={applyBoost} disabled={busy} data-testid="admin-boost-confirm">
              {busy ? "..." : `Boost for ${boostDays} days`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Tile({ label, value, accent, testid }) {
  const colors = {
    primary: "from-primary/10 to-primary/5 border-primary/20",
    emerald: "from-emerald-500/10 to-emerald-500/5 border-emerald-500/30",
    amber: "from-amber-500/10 to-amber-500/5 border-amber-500/30",
    blue: "from-blue-500/10 to-blue-500/5 border-blue-500/30",
    indigo: "from-indigo-500/10 to-indigo-500/5 border-indigo-500/30",
    rose: "from-rose-500/10 to-rose-500/5 border-rose-500/30",
    purple: "from-purple-500/10 to-purple-500/5 border-purple-500/30",
    slate: "from-slate-500/10 to-slate-500/5 border-slate-500/30",
  }[accent] || "from-primary/10 to-primary/5 border-primary/20";
  return (
    <div className={`bg-gradient-to-br ${colors} border rounded-lg p-4`} data-testid={testid}>
      <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{label}</p>
      <p className="font-display text-3xl font-black tracking-tighter text-primary mt-1">{count(value)}</p>
    </div>
  );
}
